import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { isWeakPassword, mapApiError, mapPasswordViolations } from '@sentinel/auth-core';
import { confirmSchema, type ConfirmInput } from '../features/reset-password/schema';
import { useConfirmReset } from '../features/reset-password/use-confirm';

interface ErrorBody {
  error?: string;
  message?: string;
}

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody; detail?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe.detail ?? maybe;
  }
  return undefined;
}

type ViewState = 'idle' | 'expired';

function ResetPasswordConfirmPage() {
  const { t } = useTranslation('reset-password');
  const { token: routeToken } = Route.useParams();
  // Capture once: replaceState below blows the param away from history,
  // but we still need to submit it.
  const tokenRef = useRef(routeToken);
  const token = tokenRef.current;
  const confirm = useConfirmReset();
  const [view, setView] = useState<ViewState>('idle');
  const [topError, setTopError] = useState<string | null>(null);

  // Strip the token from history so a Back button / refresh / shared
  // history sync cannot replay the credential.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history.replaceState) return;
    const cleanPath = window.location.pathname.replace(/\/[^/]+$/, '/_consumed');
    window.history.replaceState(null, '', cleanPath + window.location.search);
  }, []);

  const form = useForm<ConfirmInput>({ resolver: zodResolver(confirmSchema) });

  async function onSubmit(values: ConfirmInput) {
    setTopError(null);
    try {
      await confirm.mutateAsync({ token, new_password: values.new_password });
      // The /login route's search schema only accepts `return_to`. Adding
      // `?reset=success` would fail validation, so we do a hard navigation
      // instead. TODO: render success banner on /login when ?reset=success.
      window.location.assign('/login?reset=success');
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      // Backend returns a stable `error: "token_invalid"` code for
      // invalid/expired reset tokens (deliberately one code: leaking the
      // distinction would be an enumeration oracle). The legacy 401 path
      // is retained for safety in case middleware ever 401s the request.
      if (status === 401 || body?.error === 'token_invalid') {
        setView('expired');
        return;
      }
      if (isWeakPassword(body)) {
        const items = mapPasswordViolations(body);
        const message = items
          .map((it) => t(it.i18nKey, { ns: 'common', ...it.params }))
          .join(' ');
        form.setError('new_password', { type: 'policy', message });
        setTopError(null);
        return;
      }
      if (status === 400) {
        // Server-driven validation messages (e.g. weak password rules).
        // Never surface raw body.message; map to a known code.
        const errKey = mapApiError(body);
        setTopError(t(errKey, { ns: 'common' }));
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  const confirmErrorRaw = form.formState.errors.confirm_password?.message;
  const confirmErrorMsg =
    confirmErrorRaw === 'passwords_must_match' ? t('passwords_must_match') : confirmErrorRaw;

  if (view === 'expired') {
    return (
      <AuthCard title={t('expired_title')} subtitle={t('expired_message')}>
        <p>
          <Link to="/reset-password">{t('request_new_link')}</Link>
        </p>
        <p style={{ textAlign: 'center' }}>
          <Link to="/login">{t('back_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('confirm_title')} subtitle={t('confirm_subtitle')}>
      {topError ? (
        <Alert type="error" role="alert">{topError}</Alert>
      ) : null}

      <form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'grid', gap: '0.75rem' }}>
        <FieldRow>
          <Label htmlFor="new_password">{t('new_password_label')}</Label>
          <Input
            id="new_password"
            type="password"
            autoComplete="new-password"
            aria-invalid={form.formState.errors.new_password ? 'true' : undefined}
            {...form.register('new_password')}
          />
          <FieldError message={form.formState.errors.new_password?.message} />
        </FieldRow>

        <FieldRow>
          <Label htmlFor="confirm_password">{t('confirm_password_label')}</Label>
          <Input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            aria-invalid={form.formState.errors.confirm_password ? 'true' : undefined}
            {...form.register('confirm_password')}
          />
          <FieldError message={confirmErrorMsg} />
        </FieldRow>

        <Button type="submit" disabled={confirm.isPending}>
          {t('confirm_submit')}
        </Button>
      </form>

      <p style={{ textAlign: 'center' }}>
        <Link to="/login">{t('back_to_login')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/reset-password/$token')({
  component: ResetPasswordConfirmPage,
});
